import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Space, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useToast } from './Toast';
import { getDeviceId } from '../utils/deviceId';
import { getNetworkStatus } from '../utils/capacitorNetwork';
import { getErrorMessage } from '../utils/errorHandling';
import offlineAuthService from '../services/offlineAuthService';
import { ROUTES } from '../constants/routes';

const { Title } = Typography;

const Login = ({ onLogin }) => {
  const [pin, setPin] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  // Check network status on mount
  React.useEffect(() => {
    const checkNetwork = async () => {
      try {
        const networkStatus = await getNetworkStatus();
        setIsOffline(!networkStatus.connected);
      } catch (e) {
        setIsOffline(!navigator.onLine);
      }
    };
    checkNetwork();
    
    // Listen for network changes
    const interval = setInterval(checkNetwork, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async () => {
    if (pin.length < 4) return;
    setLoading(true);
    try {
      // Check if online
      let isOnline = true;
      try {
        const networkStatus = await getNetworkStatus();
        isOnline = networkStatus.connected;
      } catch (e) {
        isOnline = navigator.onLine;
      }

      // If offline, try offline login
      if (!isOnline) {
        const offlineResult = await offlineAuthService.verifyOfflineLogin(operatorId, pin);
        
        if (offlineResult.success) {
          const { operator } = offlineResult;
          const authId = operator.operator_code;
          const auth = { operator_id: authId, pin: pin }; // Use actual PIN for when online
          
          // Store in localStorage for compatibility
          localStorage.setItem('operatorAuth', JSON.stringify(auth));
          localStorage.setItem('operatorData', JSON.stringify(operator));
          
          onLogin(operator, auth);
          toast.success(`Offline login successful - ${operator.name}`);

          // Always go to role selection after successful login (hard rule)
          setTimeout(() => {
            navigate(ROUTES.SELECT_ROLE, { replace: true });
          }, 100);
          return;
        } else {
          toast.error(offlineResult.error || 'Invalid credentials. Please connect to internet for first-time login.');
          setLoading(false);
          return;
        }
      }

      // Online login
      // Get device identifier (MAC address or device fingerprint)
      const deviceId = await getDeviceId();
      const cleanOperatorId = operatorId ? operatorId.trim() : '';
      const cleanPin = pin ? pin.trim() : '';

      const res = await api.post('/login/', {
        pin: cleanPin,
        operator_id: cleanOperatorId || undefined,
        mac_address: deviceId.mac_address || undefined,
        device_fingerprint: deviceId.device_fingerprint || undefined,
      });
      const { operator, token } = res.data;
      
      // Cache credentials for offline access
      await offlineAuthService.cacheOperatorCredentials(operator, cleanPin, deviceId);
      
      // Always use text-based operator_code for auth headers
      const authId = operator.operator_code;
      const auth = { operator_id: authId, pin: token };
      localStorage.setItem('operatorAuth', JSON.stringify(auth));
      localStorage.setItem('operatorData', JSON.stringify(operator));
      onLogin(operator, auth);

      // Hard rule: after successful online login, always go to role selection
      setTimeout(() => {
        navigate(ROUTES.SELECT_ROLE, { replace: true });
      }, 100);
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = getErrorMessage(err, 'Invalid PIN or operator');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)',
      padding: '16px'
    }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        {isOffline && (
          <Alert
            message="📴 Offline Mode"
            description="You are offline. Login will use cached credentials. First-time login requires internet connection."
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}
        <Card 
        style={{ 
          width: '100%', 
          maxWidth: '400px',
          boxShadow: '0 10px 25px rgba(14, 165, 233, 0.15)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%', alignItems: 'center' }}>
          <img 
            src="/logo.jpeg" 
            alt="JAY COPPER & ALLOYS PVT. LTD." 
            style={{ 
              maxWidth: '200px', 
              width: '100%', 
              height: 'auto',
              marginBottom: '8px'
            }} 
          />
          <Title level={2} style={{ textAlign: 'center', margin: 0 }}>
            Operator Login
          </Title>
          
          <Form layout="vertical" size="large">
            <Form.Item 
              label="Operator ID"
              rules={[{ required: true, message: 'Operator ID is required' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="e.g., OP-01"
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                allowClear
              />
            </Form.Item>
            
            <Form.Item 
              label="PIN"
              rules={[{ required: true, min: 4, message: 'PIN must be at least 4 digits' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter PIN"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                inputMode="numeric"
              />
            </Form.Item>
            
            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="primary" 
                block 
                size="large"
                onClick={handleLogin} 
                loading={loading}
                disabled={pin.length < 4 || operatorId.trim().length === 0}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
      </div>
    </div>
  );
};

export default Login;

